import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EnvelopeIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, ConfirmModal, RowAction, Table, Tag, Textarea } from "../ui";
import { errorMessageOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { SCHOLARSHIPS_KEY } from "./model";
import { formatDateTime } from "./review/model";
import { canManageScholarships } from "./access";

export const invitationsKey = (id) => [...SCHOLARSHIPS_KEY, "invitations", id];
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Emails pasted one per line, or separated by commas, semicolons or spaces; unique, ignoring case
const emailsIn = (text) => [...new Set(text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];

const statusOf = (i) =>
  i.applied ? <Tag color="green">Applied</Tag> : !i.sentAt ? <Tag color="gray">Sent when published</Tag> : i.hasAccount ? <Tag color="blue">Invited</Tag> : <Tag color="yellow">Invited, no account yet</Tag>;

/**
 * The students a private scholarship is for, by email. Invitations go out when it is published (at
 * once if it is): students with an account are told in the app and by email, others by email
 * asking them to create an account with that address first.
 */
export default function InvitationsTab({ scholarship }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const editable = canManageScholarships() && ["DRAFT", "PUBLISHED"].includes(scholarship.state);

  const { data: invitations = [], isPending } = useQuery({
    queryKey: invitationsKey(scholarship.id),
    queryFn: async () => (await scholarshipsApi.get(`admin/scholarships/${scholarship.id}/invitations`)).data,
  });

  const invite = async () => {
    const emails = emailsIn(text);
    const invalid = emails.filter((e) => !EMAIL.test(e));
    if (!emails.length) return setError("Enter at least one email address.");
    if (invalid.length) return setError(`Not valid: ${invalid.join(", ")}`);
    setError(null);
    setBusy(true);
    try {
      const { data } = await scholarshipsApi.post(`admin/scholarships/${scholarship.id}/invitations`, { emails });
      queryClient.setQueryData(invitationsKey(scholarship.id), data.invitations);
      const skipped = emails.length - data.added;
      const when = scholarship.state === "PUBLISHED" ? "sent" : "sent when the scholarship is published";
      toast.success(`${data.added} invitation${data.added === 1 ? "" : "s"} ${when}${skipped ? `; ${skipped} already invited` : ""}`);
      setText("");
    } catch (err) {
      console.error(`Failed to invite students to scholarship ${scholarship.id}:`, err);
      const problem = problemOf(err);
      // The API names the bad ones by position
      const bad = (problem?.errors ?? []).map((e) => /^emails\[(\d+)\]$/.exec(e.field ?? "")?.[1]).filter(Boolean).map((i) => emails[i]);
      setError(bad.length ? `Not valid: ${bad.join(", ")}` : null);
      toast.error(errorMessageOf(err, "Failed to send the invitations. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await scholarshipsApi.delete(`admin/scholarships/${scholarship.id}/invitations/${removing.id}`);
      queryClient.setQueryData(invitationsKey(scholarship.id), (list = []) => list.filter((i) => i.id !== removing.id));
      toast.success(`Invitation for ${removing.email} withdrawn`);
      setRemoving(null);
    } catch (err) {
      console.error(`Failed to withdraw invitation ${removing.id}:`, err);
      toast.error(errorMessageOf(err, "Failed to withdraw the invitation. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: "email", label: "Email", accessor: (i) => i.email, render: (i) => <span className="font-medium text-slate-800">{i.email}</span> },
    { key: "status", label: "Status", accessor: (i) => (i.applied ? "Applied" : i.sentAt ? "Invited" : "Not sent yet"), render: statusOf },
    { key: "sentAt", label: "Sent", accessor: (i) => (i.sentAt ? formatDateTime(i.sentAt) : "—"), sortValue: (i) => i.sentAt ?? "" },
    ...(editable ? [{ key: "remove", label: "", exportable: false, render: (i) => <RowAction Icon={TrashIcon} label="Withdraw" onClick={() => setRemoving(i)} disabled={busy} /> }] : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      {editable && (
        <div className="flex flex-col gap-3">
          <Textarea
            label="Invite by email"
            rows={4}
            placeholder={"One email per line, or separated by commas\nstudent@example.com"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            error={error}
            disabled={busy}
          />
          <Button icon={<EnvelopeIcon className="h-4 w-4" />} onClick={invite} loading={busy && !removing} disabled={!text.trim()} className="w-fit">
            {scholarship.state === "PUBLISHED" ? "Send invitations" : "Add invitations"}
          </Button>
        </div>
      )}
      <Table columns={columns} data={invitations} rowKey={(i) => i.id} isLoading={isPending} exportable={false} emptyTitle="No students invited yet" />
      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title={`Withdraw the invitation for ${removing?.email ?? ""}?`}
        description="They will no longer see the scholarship. If they already applied, they keep their application."
        confirmLabel="Withdraw"
      />
    </div>
  );
}
